import { Fragment, useMemo, useState } from "react";
import { GRAMMAR_GLOSSARY, type GlossaryEntry } from "./grammarGlossary";

// Renders plain text, but turns any grammar term from the glossary into an
// inline term with a hover tooltip (definition + examples). Lets students
// who don't recognise a term ("temporal expression", "preposition") get a
// quick explanation without leaving the exercise.

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Italian plural of a single word: -o/-e → -i, -a → -e (e.g. preposizione →
// preposizioni, articolo → articoli, espressione → espressioni). Returns null
// when no rule applies (English words mostly fall through harmlessly).
function italianPlural(word: string): string | null {
  if (/[oe]$/.test(word)) return word.slice(0, -1) + "i";
  if (/a$/.test(word)) return word.slice(0, -1) + "e";
  return null;
}

// All surface forms a phrase might appear as: itself, the English +s plural,
// and the Italian plural of its final word. Multi-word Italian phrases whose
// earlier words also inflect ("articoli determinativi") list both forms
// explicitly as aliases in the glossary.
function surfaceForms(phrase: string): string[] {
  const forms = new Set<string>([phrase, phrase + "s"]);
  const words = phrase.split(" ");
  const ip = italianPlural(words[words.length - 1]);
  if (ip) forms.add([...words.slice(0, -1), ip].join(" "));
  return [...forms];
}

// Build a lookup from every matchable surface form to its entry, plus one
// combined regex. Longest forms first so "past participle" wins over
// "participle" and "preposizioni" over any shorter overlap.
const { lookup, matcher } = (() => {
  const lookup = new Map<string, GlossaryEntry>();
  for (const entry of GRAMMAR_GLOSSARY) {
    for (const phrase of [entry.term, ...(entry.aliases ?? [])]) {
      for (const form of surfaceForms(phrase.toLowerCase())) {
        if (!lookup.has(form)) lookup.set(form, entry);
      }
    }
  }
  const forms = [...lookup.keys()].sort((a, b) => b.length - a.length);
  const matcher = new RegExp(`\\b(${forms.map(escapeRegExp).join("|")})\\b`, "gi");
  return { lookup, matcher };
})();

function GlossaryTerm({ label, entry }: { label: string; entry: GlossaryEntry }) {
  const [tapped, setTapped] = useState(false);
  return (
    <span className="group relative inline-block">
      <span
        className="cursor-help underline decoration-dotted decoration-text-tertiary/60 underline-offset-[3px] text-text-secondary"
        onClick={() => setTapped((v) => !v)}
      >
        {label}
      </span>
      <span
        role="tooltip"
        className={`absolute bottom-full left-0 mb-2 w-[min(15rem,calc(100vw-2rem))] sm:left-1/2 sm:-translate-x-1/2 card p-3 text-left transition-opacity duration-200 z-50 normal-case ${
          tapped ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        } pointer-events-none`}
      >
        <span className="block text-[0.7rem] uppercase tracking-[0.1em] font-semibold text-accent mb-1">
          {entry.term}
        </span>
        <span className="block text-[0.78rem] text-text-secondary leading-relaxed">
          {entry.definition}
        </span>
        <span className="block mt-1.5 text-[0.72rem] text-text-tertiary leading-relaxed font-display italic">
          {entry.examples.join(" · ")}
        </span>
      </span>
    </span>
  );
}

export default function GlossaryText({ text }: { text: string }) {
  const nodes = useMemo(() => {
    const out: React.ReactNode[] = [];
    let last = 0;
    matcher.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = matcher.exec(text)) !== null) {
      const entry = lookup.get(m[1].toLowerCase());
      if (!entry) continue;
      if (m.index > last) out.push(<Fragment key={last}>{text.slice(last, m.index)}</Fragment>);
      out.push(<GlossaryTerm key={m.index} label={m[0]} entry={entry} />);
      last = m.index + m[0].length;
    }
    if (last < text.length) out.push(<Fragment key={last}>{text.slice(last)}</Fragment>);
    return out;
  }, [text]);

  return <>{nodes}</>;
}
